import React, { useState, useEffect, useContext, useCallback } from 'react';
import { DataGrid, GridToolbar } from '@mui/x-data-grid';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  Tooltip,
} from '@mui/material';
import { Edit, Delete } from '@mui/icons-material';
import PropTypes from 'prop-types';

import axiosInstance from '../../api';
import { authContext } from '../../routes';
import StyledCard from '../../components/card/StyledCard';
import Iconify from '../../components/iconify/Iconify';
import SERVER_API_URL from '../../config';
import LocaleText from '../../utils/localText';
import { ethDate } from '../../utils/ethDate';

export default function CategoryList({ setCategoryToBeUpdated, setCategoryActivatedOn }) {
  const { user } = useContext(authContext);
  const [categoryList, setCategoryList] = useState([]);
  const [openDelete, setOpenDelete] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState();

  const columns = [
    {
      headerClassName: 'super-app-theme--header', field: 'color',
      width: 100,
      headerName: 'ቀለም',
      renderCell: (params) => <Iconify sx={{ color: params && params.value }} icon="ic:twotone-family-restroom" />,
    },
    { headerClassName: 'super-app-theme--header', field: 'name', width: 200, headerName: 'የምድብ ስም' },
    {
      headerClassName: 'super-app-theme--header', field: 'type', width: 150, headerName: 'የንስሃ አባት',
      valueGetter: (params) => (params.value === 'confessor' ? 'መሆን ይችላል' : 'መሆን አይችልም'),
    },
    { headerClassName: 'super-app-theme--header', field: 'description', width: 300, headerName: 'የምድብ መግለጫ' },
    { headerClassName: 'super-app-theme--header', field: 'usersCount', width: 150, headerName: 'ተከታዮች' },
    {
      headerClassName: 'super-app-theme--header', field: 'actions',
      width: 150,
      headerName: 'ድርጊቶች',
      renderCell: (params) => {
        const handleEdit = async () => {
          try {
            const response = await axiosInstance.get(`${SERVER_API_URL}/category/findOneById/${params.id}`);
            if (response.status === 200) {
              setCategoryToBeUpdated(response.data);
              setCategoryActivatedOn('update');
            }
          } catch (error) {
            // console.log(error)
          }
        };

        const handleDelete = async () => {
          try {
            const response = await axiosInstance.get(`${SERVER_API_URL}/category/findOneById/${params.id}`);
            if (response.status === 200) {
              setSelectedCategory(response.data);
              setOpenDelete(true);
            }
          } catch (error) {
            // console.log(error)
          }
        };

        return (
          <>
            <Tooltip title="አርትዕ" color="warning" arrow>
              <IconButton disabled={!(user && user.role.permissions.category.update)} onClick={handleEdit}>
                <Edit sx={{ fontSize: '18px' }} />
              </IconButton>
            </Tooltip>
            <Tooltip title="ሰርዝ" color="error" arrow>
              <IconButton disabled={!(user && user.role.permissions.category.delete)} onClick={handleDelete}>
                <Delete sx={{ fontSize: '18px' }} />
              </IconButton>
            </Tooltip>
          </>
        );
      },
    },
    {
      headerClassName: 'super-app-theme--header', field: 'createdAt',
      headerName: 'የተመዘገበበት ቀን',
      width: 210,
      valueGetter: (params) => ethDate(params.value),
    },
    {
      headerClassName: 'super-app-theme--header', field: 'updatedAt',
      headerName: 'የተስተካከለበት ቀን',
      width: 210,
      valueGetter: (params) => ethDate(params.value),
    },
  ];

  const handleOk = async () => {
    try {
      const response = await axiosInstance.patch(
        `${SERVER_API_URL}/category/deleteStatusOneById/${selectedCategory.id}`
      );
      if (response.status === 200) {
        getAllCategory();
        setOpenDelete(false);
      }
    } catch (error) {
      // console.log(error)
    }
  };

  const getAllCategory = useCallback(async () => {
    try {
      const response = await axiosInstance.get(`${SERVER_API_URL}/category/findAll`);

      if (response.status === 200) {
        setCategoryList(response.data);
      }
    } catch (error) {
      // console.log(error);
    }
  }, []);

  useEffect(() => {
    getAllCategory();
  }, [getAllCategory]);

  return (
    <>
      <StyledCard sx={{
        height: '75vh',
        '& .super-app-theme--header': {
          backgroundColor: (theme) => theme.palette.mode === 'dark' ? theme.palette.primary.darkMode.lighter : theme.palette.grey[400],
        }
      }}>
        <DataGrid
          sx={{ p: 2, pt: 0, height: '75vh', '& .MuiDataGrid-row': { borderTopColor: (theme) => theme.palette.secondary.darker, borderWidth: 0.5, borderTopStyle: 'dashed' } }}
          rows={categoryList}
          columns={columns}
          pageSize={12}
          initialState={{
            filter: {
              filterModel: {
                items: [],
                quickFilterValues: [''],
              },
            },
            pagination: { pageSize: 10 },
          }}
          slots={{ toolbar: GridToolbar }}
          slotProps={{
            toolbar: {
              showQuickFilter: true,
              quickFilterProps: { debounceMs: 500 },
            },
          }}
          pageSizeOptions={[5, 10, 25, 50, 100]}
          disableSelectionOnClickCell
          disableColumnFilter
          localeText={LocaleText.pagination}
        />
      </StyledCard>
      <Dialog open={openDelete} onClose={() => setOpenDelete(false)}>
        <Box sx={{ backgroundColor: (theme) => theme.palette.mode === 'light' ? '#F9F9FB' : '#272424' }}>
          <DialogTitle>እርግጠኛ ነህ ምድብ {selectedCategory && selectedCategory.name}ን መሰረዝ ትፈልጋለህ?</DialogTitle>
          <DialogContent>
            <DialogContentText>በዚህ ምድብ ስር ያሉ ሁሉም ተጠቃሚዎች ይጠፋሉ!</DialogContentText>
            <DialogContentText>ይህ እርምጃ ሊቀለበስ አይችልም።</DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setOpenDelete(false)}>ተመለስ</Button>
            <Button color="error" variant="contained" onClick={handleOk} autoFocus>
              ሰርዝ
            </Button>
          </DialogActions>
        </Box>
      </Dialog>
    </>
  );
}

CategoryList.propTypes = {
  setCategoryToBeUpdated: PropTypes.func.isRequired,
  setCategoryActivatedOn: PropTypes.func.isRequired,
};
